/// <amd-module name="Repos/Views/CommitsHub/Components/CommitsHub" />
import * as React from "react";
import { ICommitsDataProviderService } from "Repos/CommitsList/CommitsDataProviderService";
import { CommitsFilterKeys, getInitialCommitsFilterBarState } from "Repos/CommitsList/Components/CommitsFilterBar";
import { CommitsHistoryView } from "Repos/CommitsList/Components/CommitsHistoryView";
import { CommitsTable } from "Repos/CommitsList/Components/CommitsTable";
import { GitStatus, GitVersionDescriptor, GitVersionType } from "Repos/Common/Contracts/Git";
import { ICommit } from "Repos/Common/Contracts/History";
import { getDefaultCreateBranchAction } from "Repos/Common/ReposActionService";
import { branchNameToRef, getShortCommitId } from "Repos/Common/Util/Ref";
import { getVersionString, gitVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService, setUserDefaultVersion } from "Repos/Common/VCViewModelDataProviderService";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import * as Resources from "Repos/Views/CommitsHub/Resources";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import * as String from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { FastPageSwitch, onClickFPS } from "VSS/Platform/FPS";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Card } from "VSSUI/Card";
import { CustomHeader, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITextFieldProps, TextField, TextFieldFocusTreatmentBehavior, TextFieldStyle, TextFieldWidth } from "VSSUI/TextField";
import { css, KeyCode } from "VSSUI/Util";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ClipboardButton } from "VSSUI/Clipboard";
import * as ClipboardResources from "VSSUI/Resources.Clipboard";

class CommitsHub extends VssComponent<{}> {
    public render() {
        return <CommitsHubInternal />;
    }
}

interface ICommitsHubProps { }
interface ICommitsHubState {
    commitsFrom: ICommit[] | undefined;
    hasMore: boolean | undefined;
    showGraph: boolean;
}

const hideGraphSettingsKey = "Repos.CommitsHub.HideGraph";

class CommitsHubInternal extends VssComponent<ICommitsHubProps, ICommitsHubState> {
    private filter = new Filter();
    private filterVisible = new ObservableValue<boolean>(false);
    private loaded: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private commitsDataService: ICommitsDataProviderService;
    private searchValue = new ObservableValue("");
    private pageHistoryService: IPageStateHistoryService;
    private messagesService: IReposMessagesService;
    private refName: string | undefined;
    private version: GitVersionDescriptor;

    constructor(props: ICommitsHubProps, context: IVssComponentContext) {
        super(props, context);

        const initialFilterState = getInitialCommitsFilterBarState(this.context.pageContext);
        if (Object.keys(initialFilterState).length > 0) {
            this.filterVisible.value = true;
        }

        const defaultFilterState: IFilterState = {};
        defaultFilterState[CommitsFilterKeys.HistoryType] = { value: 0 };
        this.filter.setDefaultState(defaultFilterState);
        this.filter.setState(initialFilterState, true);
        this.pageHistoryService = this.context.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");

        this.commitsDataService = context.pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");
        this.messagesService = this.context.pageContext.getService<IReposMessagesService>("IReposMessagesService");

        const historyState = this.pageHistoryService.getProjectedState();
        if (historyState.commitId) {
            this.searchValue.value = historyState.commitId;
            this.doCommitFromSearch();
        }
        const vcViewModelDataProviderService = this.context.pageContext.getService<IVCViewModelDataProviderService>(
            "IVCViewModelDataProviderService"
        );

        this.version = gitVersionStringToVersionDescriptor(
            historyState.itemVersion || "GB" + vcViewModelDataProviderService.getDefaultGitBranchName()
        );

        if (this.version.versionType === GitVersionType.Branch) {
            this.refName = branchNameToRef(this.version.version);
        }

        this.state = { commitsFrom: undefined, hasMore: undefined, showGraph: tryGetLocalStorageValue(hideGraphSettingsKey) !== "true" };
    }

    public render() {
        // Ensure a deep copy. Just doing props.commits.push is triggering the observer to rerender.
        let commitsWithLoading: (ObservableValue<ICommit | undefined> | ICommit)[] | undefined = this.state.commitsFrom;
        if (commitsWithLoading && this.state.hasMore) {
            commitsWithLoading = commitsWithLoading.map(x => x);
            commitsWithLoading.push(new ObservableValue(undefined));
        }
        const commitTextFieldProps: ITextFieldProps = {
            ariaLabel: Resources.SearchCommitId,
            className: "bolt-text-filterbaritem flex-grow",
            containerClassName: "flex-grow",
            inputClassName: "bolt-text-filterbaritem-input",
            focusTreatment: TextFieldFocusTreatmentBehavior.none,
            maxLength: 40,
            onKeyDown: this.onKeyDown,
            onChange: this.onTextChanged,
            placeholder: Resources.SearchCommitId,
            style: TextFieldStyle.inline,
            suffixIconProps: {
                iconName: "Search",
                onClick: this.onSearchClick
            },
            value: this.searchValue,
            width: TextFieldWidth.tabBar,
            role: "search"
        }
        return (
            <Surface background={SurfaceBackground.neutral}>
                <ScreenSizeObserver>
                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                        const commandBarItems: IHeaderCommandBarItem[] = !this.state.commitsFrom
                            ? [
                                {
                                    ariaLabel: Resources.ShowFilter,
                                    iconProps: { iconName: "Filter" },
                                    id: "filter",
                                    onActivate: () => (this.filterVisible.value = !this.filterVisible.value),
                                    subtle: true
                                }
                            ]
                            : [];

                        const isExtraSmallWidth = screenSizeProps.screenSize === ScreenSize.xsmall;
                        if (!isExtraSmallWidth && !this.state.commitsFrom) {
                            const graphLabel = this.state.showGraph ? Resources.GraphShowingTooltip : Resources.GraphHiddenTooltip;
                            commandBarItems.push({
                                ariaLabel: graphLabel,
                                iconProps: { iconName: "GitGraph" },
                                id: "showGraph",
                                onActivate: this.onShowGraph,
                                text: this.state.showGraph ? Resources.GraphOn : Resources.GraphOff,
                                tooltipProps: { text: graphLabel }
                            });
                        }
                        return (
                            <Page className="flex-grow custom-scrollbar scroll-auto-hide">
                                <div className="flex-row flex-noshrink repos-commits-hub-version-dropdown flex-wrap">
                                    <VersionDropdown
                                        initiallySelectedVersion={this.version}
                                        onSelect={this.onNewVersionSelected}
                                        providerOptions={{ onCreateBranchClick: getDefaultCreateBranchAction(this.context.pageContext) }}
                                        className={css(isExtraSmallWidth && "flex-grow")}
                                    />
                                    <ClipboardButton
                                        className="repos-commits-branch-copy-button"
                                        getContent={() => 
                                            (this.version.version)
                                        }
                                        key="clipboard"
                                        showCopiedTooltip={true}
                                        tooltipProps={{ text: ClipboardResources.CopyToClipboard }}
                                    />  
                                    {isExtraSmallWidth && <div className="flex-row margin-top-4 margin-left-8 flex-grow">
                                        <TextField {...commitTextFieldProps} />
                                    </div>}
                                </div>
                                <CustomHeader className="repos-commits-hub-header">
                                    <div className="bolt-header-content-area flex-row flex-grow flex-self-stretch">
                                        <HeaderTitleArea>
                                            <HeaderTitleRow>
                                                <HeaderTitle titleSize={TitleSize.Large}>
                                                    {this.state.commitsFrom && this.state.commitsFrom.length
                                                        ? String.format(Resources.SearchCommitHeader, this.searchValue.value)
                                                        : Resources.CommitsPageHeader}
                                                </HeaderTitle>
                                            </HeaderTitleRow>
                                        </HeaderTitleArea>
                                        <HeaderCommandBar items={commandBarItems} className={css(isExtraSmallWidth && "margin-right-4")} />
                                        {!isExtraSmallWidth && <div className="flex-row margin-left-8">
                                            <TextField {...commitTextFieldProps} />
                                        </div>}
                                    </div>
                                </CustomHeader>

                                <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                                    <ReposMessages className="flex-noshrink" />

                                    {!commitsWithLoading ? (
                                        <CommitsHistoryView
                                            filterVisible={this.filterVisible}
                                            shouldUpdateUrl={true}
                                            showGraph={this.state.showGraph}
                                            version={this.version}
                                        />
                                    ) : (
                                            <Card className="bolt-table-card" contentProps={{ contentPadding: false }}>
                                                <CommitsTable
                                                    dataLoaded={this.loaded}
                                                    commits={commitsWithLoading}
                                                    showHeader={true}
                                                    showCommitDetails={true}
                                                    loadMore={this.state.hasMore ? this.doCommitFromSearch : undefined}
                                                />
                                            </Card>
                                        )}
                                </div>
                            </Page>
                        );
                    }}
                </ScreenSizeObserver>
            </Surface>
        );
    }

    public componentDidMount() {
        this.handleLoaded();
    }

    private onTextChanged = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        this.searchValue.value = value;
    };

    private onNewVersionSelected = (event: React.SyntheticEvent<HTMLElement>, item: any): void => {
        const currentVersionString = getVersionString(this.version);

        const newVersion = item.data.versionDescriptor;
        const newVersionString = getVersionString(newVersion);

        if (currentVersionString !== newVersionString) {
            const newVersionUrl = this.commitsDataService.getCommitsUrl(newVersion);
            onClickFPS(this.context.pageContext, newVersionUrl, true, event);
            setUserDefaultVersion(this.context.pageContext, newVersion);
        }
    };

    private onSearchClick = (event?: React.MouseEvent<HTMLSpanElement>): void => {
        this.loaded.value = false;
        this.doCommitFromSearch(true);
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        switch (ev.which) {
            case KeyCode.enter:
                this.onSearchClick();
                break;

            case KeyCode.escape:
                this.searchValue.value = "";
                this.setState({ commitsFrom: undefined });
                break;

            default:
                return;
        }

        // We only get here if the keypress has been handled.
        ev.preventDefault();
        ev.stopPropagation();
    };

    private doCommitFromSearch = (newSearch?: boolean) => {
        const value = this.searchValue.value;
        if (value === "") {
            const historyState = this.pageHistoryService.getProjectedState();
            delete historyState.commitId;
            this.pageHistoryService.queueState(historyState, HistoryBehavior.newEntry);

            this.setState({ commitsFrom: undefined });
            return;
        } else {
            this.commitsDataService
                .fetchCommitsFrom(value, this.loaded.value && !newSearch && this.state.commitsFrom ? this.state.commitsFrom.length : 0)
                .then(commitResults => {
                    if (commitResults && commitResults.commits.length === 1) {
                        FastPageSwitch(
                            this.context.pageContext,
                            this.commitsDataService.getCommitUrl(commitResults.commits[0].commitId, this.refName),
                            true
                        );
                    } else if (commitResults && commitResults.commits.length > 0) {
                        const historyState = this.pageHistoryService.getProjectedState();
                        historyState["commitId"] = value;
                        this.pageHistoryService.queueState(historyState, HistoryBehavior.newEntry);

                        let newCommits = commitResults.commits.map((c, index) => {
                            const commit: ICommit = {
                                linkProps: { href: this.commitsDataService.getCommitUrl(c.commitId, this.refName) },
                                shortCommitId: getShortCommitId(c.commitId),
                                comment: c.comment || "",
                                commit: c,
                                repositoryId: commitResults.repository,
                                repositoryName: commitResults.repositoryName,
                                projectName: commitResults.project,
                                pullRequest: new ObservableValue(undefined),
                                statuses: new ObservableArray<GitStatus>([])
                            };
                            return commit;
                        });

                        if (!newSearch && this.state.commitsFrom && this.state.commitsFrom.length > 0) {
                            newCommits = this.state.commitsFrom.concat(newCommits);
                        }

                        this.setState({ commitsFrom: newCommits });

                        this.commitsDataService
                            .fetchCommitDetails(
                                commitResults.commits.map(commit => commit.commitId),
                                this.version
                            )
                            .then(fullCommitResults => {
                                if (fullCommitResults && fullCommitResults.pullRequests && this.state.commitsFrom) {
                                    for (const commitId in fullCommitResults.pullRequests) {
                                        const commit = this.state.commitsFrom.find(commit => commit.commit.commitId === commitId);
                                        if (commit && commit.pullRequest) {
                                            commit.pullRequest.value = fullCommitResults.pullRequests[commitId];
                                        }
                                    }

                                    fullCommitResults.commits.forEach(commitRef => {
                                        if (this.state.commitsFrom) {
                                            const commit = this.state.commitsFrom.find(commit => commit.commit.commitId === commitRef.commitId);
                                            if (commit && commit.statuses) {
                                                if (commitRef.statuses) {
                                                    commit.statuses.value = commitRef.statuses;
                                                }
                                            }
                                        }
                                    });
                                }
                            });
                        this.setState({ hasMore: commitResults.hasMore });
                    } else {
                        const historyState = this.pageHistoryService.getProjectedState();
                        delete historyState.commitId;
                        this.pageHistoryService.queueState(historyState, HistoryBehavior.newEntry);
                        const searchVal = this.searchValue.value;
                        this.messagesService.showMessage({
                            renderMessage: () => <span>{String.format(Resources.NoCommitsMatch, searchVal)}</span>,
                            severity: MessageCardSeverity.Error
                        });

                        this.searchValue.value = "";
                        this.setState({ commitsFrom: undefined });
                    }
                });

            this.loaded.value = true;
        }
    };

    private handleLoaded() {
        if (!this.loaded.value) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive("Repos", "commits-hub");
            this.loaded.value = true;
        }
    }

    private onShowGraph = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const showGraph = !this.state.showGraph;
        trySetLocalStorageValue(hideGraphSettingsKey, showGraph ? undefined : "true");
        this.setState({ showGraph });
    };
}

Components.add("ms.vss-code-web.commits-hub", CommitsHub);
